import { getAccountProgramUniversalPricing } from "~/utils/payment";

export class PaymentsService {
  constructor(axios, services, store, apiUrl) {
    this.$axios = axios;
    this.$services = services;
    this.$store = store;
    this.apiUrl = apiUrl;
  }

  getAchOptionNotAvailable() {
    const creditCardTypes = this.$store.state.persistedState?.branchInfo?.UPWebsite?.CreditCardTypes;

    return !Array.isArray(creditCardTypes) || !creditCardTypes.some(
      creditCardType => typeof creditCardType === "string" && creditCardType.trim().toUpperCase() === "ACH"
    );
  }

  async getAutoPayUrl(params) {
    return await this.$axios
      .post('/api/rg3p/GetAutoPayToken', params)
      .then(function(response) { 
        return response.data;
      })
      .catch(function(error) {
        return { error: error };
      });
  }

  async getPaymentUrl(params) {
    return await this.$axios
      .post('/api/rg3p/GetToken', params)
      .then(function(response) { 
        return response.data;
      })
      .catch(function(error) {
        return { error: error };
      });
  }

  async getPaymentId(data) {
    return await this.$axios
      .post('/api/rg3p/payment', data)
      .then(function(response) { 
        return response.data;
      })
      .catch(function(error) {
        return { error: error };
      });
  }

  async postPaymentResp(paymentId, data) {
    return await this.$axios
      .post(
        this.apiUrl + `Payments/Payment/${paymentId}/Response`,
        data
      )
      .then(function(response) { 
        return response.data;
      })
      .catch(function(error) {
        return { error: error };
      });
  }

  async postAutoPayPaymentResp(paymentId, data) {
    return await this.$axios
      .post(
        this.apiUrl + `Payments/Payment/${paymentId}/AutoPay/Response`,
        data
      )
      .then(function(response) { 
        return response.data;
      })
      .catch(function(error) {
        return { error: error };
      });
  }

  async enrollAutoPay(sqldbid, custNo, data) {
    return await this.$axios
      .post(
        this.apiUrl +
          `Payments/AutoPay/SG/Company/${sqldbid}/Customer/${custNo}/Enroll`,
        data
      )
      .then(function(response) {
        return response.data;
      })
      .catch(function(error) {
        return { error: error, response: error.response.data };
      });
  }

  async payByCreditCard(data, sessionId) {
    return await this.$axios
      .post(this.apiUrl + `Payments/Payment/SG/CreditCard/Submit`, data, {
        headers: {
          "X-SessionId": sessionId
        }
      })
      .then(function(response) {
        return response.data;
      })
      .catch(function(error) {
        return { error: error, response: error.response.data };
      });
  }

  async billCustomer(data, sessionId) {
    return await this.$axios
      .post(this.apiUrl + `Payments/Payment/SG/Billed/Submit`, data, {
        headers: {
          "X-SessionId": sessionId
        }
      })
      .then(function(response) {
        return response.data;
      })
      .catch(function(error) {
        return { error: error, response: error.response.data };
      });
  }

  async searchInstallments(data) {
    return await this.$axios.post(`${this.apiUrl}Payments/Installment/Search`, data);
  }

  async createInstallment(data) {
    return await this.$axios.post(`${this.apiUrl}Payments/Installment`, data);
  }

  getLocalTaxes() {
    const salesTaxRate = this.getSalesTaxRate();
    let salesTaxString = (salesTaxRate * 100).toFixed(3);
    if (salesTaxString.endsWith("0")) {
      salesTaxString = salesTaxString.slice(0, -1);
    }
    return [
      {
        type: "sales",
        description: `Sales tax (${salesTaxString}%)`,
        value: salesTaxRate
      }
    ];
  }

  getLocalTaxesPortal() {
    const customerTax1 = this.$store.state.persistedState?.customer?.taxRate1;
    const customerTax2 = this.$store.state.persistedState?.customer?.taxRate2;
    const customerTax3 = this.$store.state.persistedState?.customer?.taxRate3;
    const combinedCustomerTax = (customerTax1 ?? 0) + (customerTax2 ?? 0) + (customerTax3 ?? 0);

    return [
      {
        type: "sales",
        description: `Sales tax (${combinedCustomerTax}%)`,
        value: combinedCustomerTax / 100
      }
    ];
  }

  getSalesTaxRate() {
    if (this.$store.state.persistedState.order?.taxRate) {
      return this.$store.state.persistedState.order.taxRate;
    }
    const zipCodesInfo = this.$store.state.persistedState.branchInfo?.ZipCodes;
    if (zipCodesInfo?.length > 0) {
      const customerZipCode = this.$store.state.persistedState.zip?.trim();
      const matchedZipCodeInfo = zipCodesInfo.find(
        zipCodeInfo => zipCodeInfo.ZipCode?.trim() == customerZipCode
      );
      if (matchedZipCodeInfo?.TaxRate) {
        return Number(matchedZipCodeInfo.TaxRate.replace(/%/, "")) / 100;
      }
    }

    return 0;
  }

  getPayTypes() {
    const payTypes = [];
    const paymentServiceStatus = this.$store.state.persistedState?.paymentInfo?.paymentServiceStatus;
    Object.keys(this.$store.state.persistedState?.paymentInfo?.paymentServiceStatus ?? {})
      .forEach((key) => {
        const order = Number.parseInt(paymentServiceStatus[key]);
        if (!!order) {
          switch (key) {
            case "status_annualized_installments":
              payTypes.push({
                name: "autoPayInstallment",
                order: order,
              });
              break;
            case "status_autopay":
              payTypes.push({
                name: "autoPay",
                order: order,
              });
              break;
            case "status_pay_after_service_invoice":
              payTypes.push({
                name: "payAfterEach",
                order: order,
              });
              break;
            case "status_prepay":
              payTypes.push({
                name: "payNow",
                order: order,
              });
              break;
            case "status_billpay":
              payTypes.push({
                name: "billPay",
                order: order,
              });
              break;
          }
        }
      });
    payTypes.sort((a, b) => a.order - b.order);
    return payTypes.map((payType) => payType.name);
  }

  getEnabledPayTypes() {
    const enabledPayTypes = new Set();

    const paymentServiceStatus = this.$store.state.persistedState?.paymentInfo?.paymentServiceStatus;

    if (!!Number.parseInt(paymentServiceStatus?.status)) {
      this.getPayTypes().forEach((payType) => {
        switch (payType) {
          case "autoPayInstallment":
            if (!!Number.parseInt(paymentServiceStatus.status_annualized_installments)) {
              enabledPayTypes.add("autoPayInstallment"); 
            }
            break;
          case "autoPay":
            if (!!Number.parseInt(paymentServiceStatus.status_autopay)) {
              enabledPayTypes.add("autoPay");
            }
            break;
          case "payAfterEach":
            if (!!Number.parseInt(paymentServiceStatus.status_pay_after_service_invoice)) {
              enabledPayTypes.add("payAfterEach");
            }
            break;
          case "payNow":
            if (!!Number.parseInt(paymentServiceStatus.status_prepay)) {
              enabledPayTypes.add("payNow");
            }
            break;
          case "billPay":
            if (!!Number.parseInt(paymentServiceStatus.status_billpay)) {
              enabledPayTypes.add("billPay");
            }
            break;
        }
      });
    }

    const branchPaymentServiceStatus = this.$store.state.persistedState.branchInfo?.UPWebsite?.PaymentBillingOptions;

    // If we have branch-specific payment service statuses, filter enabled pay types
    if (branchPaymentServiceStatus?.length > 0) {
      if (!(branchPaymentServiceStatus.some((branchPaymentService) => /auto pay monthly installment billing/i.test(branchPaymentService?.BillingPaymentDesc)))) {
        enabledPayTypes.delete("autoPayInstallment");
      }
      if (!(branchPaymentServiceStatus.some((branchPaymentService) => /auto pay after service billing/i.test(branchPaymentService?.BillingPaymentDesc)))) {
        enabledPayTypes.delete("autoPay");
      }
      if (!(branchPaymentServiceStatus.some((branchPaymentService) => /pay after service \(invoice\)/i.test(branchPaymentService?.BillingPaymentDesc)))) {
        enabledPayTypes.delete("payAfterEach");
      }
      if (!(branchPaymentServiceStatus.some((branchPaymentService) => /pay now and save/i.test(branchPaymentService?.BillingPaymentDesc)))) {
        enabledPayTypes.delete("payNow");
      }
    }

    return Array.from(enabledPayTypes);
  }

  getPayTypeName(payType) {
    switch (payType) {
      case "autoPayInstallment":
        return "Easy Monthly Payments";
      case "autoPay":
        return "Auto Pay";
      case "payAfterEach":
        return "Pay Later"; // TODO: Check verbiage
      case "payNow":
        return "Pre-Pay";
      case "billPay":
        return "Bill Pay";
      default:
        return undefined;
    }
  }

  /* cartServices parameter optional */
  getCartAllowedPayTypes(cartServices) {
    if (!cartServices) {
      cartServices = this.$services.getCartServices();
    }

    const cartAllowedPayTypes = this.getEnabledPayTypes().filter((payType) => payType !== "billPay");

    // Override payment type for Premium Package
    if (!!this.$store.state.persistedState?.packages?.isOrderPremiumPackage && this.$store.state.persistedState?.packageId) {
      return cartAllowedPayTypes.filter((payType) => ["autoPayInstallment"].includes(payType));
    }

    // Override payment type for Total Home Pest Control
    if (this.$services.hasTotalHomePestControlService(cartServices)) {
      return cartAllowedPayTypes.filter((payType) => ["autoPay"].includes(payType));
    }

    // Override payment type when only Quote Only services are in the cart (transaction total = $0.00)
    if (this.$services.hasOnlyQuoteOnlyServices(cartServices)) {
      return cartAllowedPayTypes.filter((payType) => ["autoPay", "payAfterEach"].includes(payType));
    }

    return cartAllowedPayTypes;
  }

  /* accountPrograms, programCodes, and franchiseServices parameters required */
  getAccountAllowedPayTypes(accountPrograms, programCodes, franchiseServices) {
    const accountAllowedPayTypes = this.getEnabledPayTypes();

    // Override payment type if account has Total Home Pest program
    if (accountPrograms.some((accountProgram) => /total home pest/i.test(getAccountProgramUniversalPricing(accountProgram, programCodes, franchiseServices)?.name))) {
      return accountAllowedPayTypes.filter((payType) => ["autoPay"].includes(payType));
    }

    // Override payment type if any services have been done to date
    if (accountPrograms.some((program) => (program.doneToDate ?? 0) !== 0)) {
      return accountAllowedPayTypes.filter((payType) => ["autoPay", "payAfterEach"].includes(payType));
    }

    return accountAllowedPayTypes;
  }
}
